import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import Backend from "../../../../Backend/Backend";

const backend = await new Backend().init();

export default function EditSharing({ open, onClose, user, isSuper, reload }) {
  const [newValue, setNewValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const max_sharing = Number(isSuper?.sharing ?? 0);

  useEffect(() => {
    if (open) {
      setNewValue(String(user?.sharing ?? "")); // pre-fill existing
    }
  }, [open, user]);

  const handleSave = useCallback(async () => {
    if (isLoading) return; // block double taps

    if (!newValue.trim()) {
      toast.error("Sharing cannot be empty");
      return;
    }

    // Remove % sign if user entered "30%"
    const formatted = newValue.replace("%", "");

    // Validate
    const num = parseFloat(formatted);
    if (isNaN(num)) {
      toast.error("Please enter a valid number");
      return;
    }

    if (num < 0) {
      toast.error("Cannot be negative");
      return;
    }

    if (num > max_sharing) {
      toast.error(`Max allowed: ${max_sharing}%`);
      return;
    }

    setIsLoading(true);

    const payload = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      user_id: user.id,
      token: localStorage.getItem("token"),
      username: localStorage.getItem("username"),
      user_limit: user.user_limit,
      deposit_limit: user.deposit_limit,
      deposit_limit_total: user.deposit_limit_total,
      sharing: num,
    };

    try {
      const response = await backend.edit_user_details(payload);
      if (response.error === "False") {
        toast.success(response.message);
        reload?.();
        onClose?.();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Network error, please try again");
    } finally {
      setIsLoading(false);
    }
  }, [newValue, user, max_sharing, isLoading]);

  const handleInputChange = (e) => {
    let value = e.target.value;

    // Block letters
    value = value.replace(/[^\d.%]/g, "");

    // Allow only ONE decimal
    if ((value.match(/\./g) || []).length > 1) return;

    // Allow %
    if ((value.match(/%/g) || []).length > 1) return;

    // Allow only 2 decimals after point
    if (!/^\d*\.?\d{0,2}%?$/.test(value)) return;

    setNewValue(value);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[99999]">
      <div className="bg-[var(--background)] border-2 border-[var(--hover)] rounded-xl shadow-lg w-[90%] max-w-md p-6">
        <h2 className="text-lg font-bold text-[var(--text)]">
          ✏️ Edit Sharing (%)
        </h2>

        <input
          type="text"
          inputMode="decimal"
          value={newValue}
          maxLength={3}
          onChange={handleInputChange}
          className="mt-4 w-full border border-[var(--hover)] bg-[var(--background)] text-[var(--text)] rounded-lg px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-[var(--primary)]"
          placeholder={`Max ${max_sharing}%`}
        />

        <div className="p-2 text-sm font-semibold text-[var(--text)]">Max Sharing : {max_sharing}%</div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[var(--sell)] text-[var(--white)] transition"
            disabled={isLoading}
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[var(--primary)] text-white transition disabled:opacity-60"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
